import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, Loader2, Users, AlertTriangle } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { usePatientsList, type PatientStatus } from '@/hooks/usePatients';
import { usePatientAlertSet } from '@/hooks/useClinicalAlerts';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

/**
 * Patients list page. Mirrors the v1 layout
 * (`Clinio/src/pages/clinic/Patients.tsx`):
 *   - Page header with patient count + Show archived toggle + Ny patient button
 *   - Search field (live filter)
 *   - Table: Name | CPR | Email | Phone | Actions
 *
 * Differences from v1:
 *   - CPR column shows a badge ("CPR registreret" / "—") rather than the
 *     plaintext value, because v2 stores CPR encrypted at rest.
 *   - Search hits a server-side FTS index (search_tsv) instead of filtering
 *     client-side.
 */
export function Patients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId, isMainClinicAdmin, subClinics } = useActiveClinic();

  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showSubClinicPatients, setShowSubClinicPatients] = useState(() => {
    try {
      const saved = localStorage.getItem('clinio_show_subclinic_patients');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  // Update localStorage when toggle changes
  useEffect(() => {
    try {
      localStorage.setItem('clinio_show_subclinic_patients', String(showSubClinicPatients));
    } catch {
      // localStorage not available
    }
  }, [showSubClinicPatients]);

  // Build clinic IDs array based on toggle state
  const clinicIds = useMemo(() => {
    if (!activeClinicId) return null;
    if (showSubClinicPatients && subClinics.length > 0) {
      return [activeClinicId, ...subClinics.map((c) => c.id)];
    }
    return activeClinicId;
  }, [activeClinicId, showSubClinicPatients, subClinics]);

  const filters = useMemo(
    () => ({
      search: searchTerm.trim() || undefined,
      status: (showArchived ? 'archived' : 'active') as PatientStatus,
    }),
    [searchTerm, showArchived],
  );

  const { data: patients, isLoading, error } = usePatientsList(clinicIds, filters);
  const alertPatients = usePatientAlertSet(activeClinicId);

  const count = patients?.length ?? 0;

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
                {t('patients.title')}
              </h2>
            </div>
            <div className="mt-4 flex md:ml-4 md:mt-0">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {showArchived
                    ? t('patients.count_archived', { count })
                    : t('patients.count_active', { count })}
                </span>
                {isMainClinicAdmin && subClinics.length > 0 && (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={showSubClinicPatients}
                      onChange={(e) => setShowSubClinicPatients(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800"
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      {t('patients.show_subclinic_patients')}
                    </span>
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setShowArchived((v) => !v)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {showArchived ? t('patients.show_active') : t('patients.show_archived')}
                </button>
                {!showArchived && (
                  <Button onClick={() => navigate('/patients/new')} disabled={!activeClinicId}>
                    {t('patients.new_patient')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="mt-6">
            <div className="relative max-w-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('patients.search_placeholder')}
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  aria-label={t('patients.clear_search')}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-6">
              <ErrorAlert message={t('patients.error_loading')} />
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : !patients || patients.length === 0 ? (
            <EmptyState archived={showArchived} hasSearch={Boolean(searchTerm)} />
          ) : (
            <div className="mt-8 overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <Th>{t('patients.col_pat_number')}</Th>
                      <Th>{t('patients.col_name')}</Th>
                      <Th>{t('patients.col_cpr')}</Th>
                      <Th>{t('patients.col_email')}</Th>
                      <Th>{t('patients.col_phone')}</Th>
                      {showSubClinicPatients && subClinics.length > 0 && (
                        <Th>{t('patients.col_source_clinic')}</Th>
                      )}
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">{t('patients.col_actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                    {patients.map((p) => (
                      <tr
                        key={p.id}
                        onClick={() => navigate(`/patients/${p.id}`)}
                        className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 font-mono text-sm text-gray-500 dark:text-gray-400 sm:pl-6">
                          {p.pat_number ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2">
                            {p.first_name} {p.last_name}
                            {alertPatients.has(p.id) && (
                              <span
                                title="Klinisk advarsel"
                                className="inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300"
                              >
                                <AlertTriangle className="h-3 w-3" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {p.has_cpr ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              {t('patients.cpr_registered')}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {p.email ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {p.phone ?? '—'}
                        </td>
                        {showSubClinicPatients && subClinics.length > 0 && (
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {p.clinic_name ?? '—'}
                          </td>
                        )}
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/patients/${p.id}`);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            {t('patients.see_details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-4 dark:text-white first:sm:pl-6"
    >
      {children}
    </th>
  );
}

function EmptyState({ archived, hasSearch }: { archived: boolean; hasSearch: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mt-12 text-center">
      <Users className="mx-auto h-10 w-10 text-gray-400" />
      <h3 className="mt-4 text-base font-medium text-gray-900 dark:text-white">
        {hasSearch
          ? t('patients.empty_search_title')
          : archived
            ? t('patients.empty_archived_title')
            : t('patients.empty_title')}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {hasSearch
          ? t('patients.empty_search_subtitle')
          : archived
            ? t('patients.empty_archived_subtitle')
            : t('patients.empty_subtitle')}
      </p>
    </div>
  );
}
